/**
 * TOPBAR - CHATBOT IMÓVEIS®
 * =================================================================
 * Componente de navegação superior (header) para dashboard
 *
 * Funcionalidades:
 * - Logo da marca (responsivo)
 * - Busca global (CMD/CTRL + K)
 * - Notificações com badge
 * - Acesso rápido WhatsApp
 * - Toggle de tema (dark/light)
 * - Menu de perfil do usuário
 * - Menu mobile (hamburguer → Sheet)
 *
 * Stack: React 18 + Tailwind + Radix UI + Brand Tokens
 * A11y: WCAG 2.2 AA compliant
 * Data: 2025-11-09
 */

import React, { useState } from 'react';
import { Link } from 'wouter';
import {
  Bell,
  Menu,
  MessageCircle,
  Search,
  LogOut,
  User as UserIcon,
  Settings,
  Globe,
} from 'lucide-react';
import { ThemeToggle } from '@/shared/components/ui/ThemeToggle';
import { useFirebaseAuth } from '@/contexts/FirebaseAuthContext';

// Radix UI Components (shadcn/ui)
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';

interface TopbarProps {
  /** Callback para abrir CommandMenu (CMD+K) */
  onSearchClick?: () => void;
  /** Contador de notificações não lidas */
  notificationCount?: number;
  /** Renderizar conteúdo da Sidebar no mobile Sheet */
  sidebarContent?: React.ReactNode;
}

/**
 * Componente Topbar - Barra superior do dashboard
 *
 * Uso:
 * ```tsx
 * <Topbar
 *   onSearchClick={() => setCommandMenuOpen(true)}
 *   notificationCount={3}
 *   sidebarContent={<Sidebar />}
 * />
 * ```
 */
// Idiomas disponíveis
const LANGUAGES = [
  { code: 'pt-BR', name: 'PT-BR', flag: '🇧🇷' },
  { code: 'pt-PT', name: 'PT-PT', flag: '🇵🇹' },
  { code: 'en-IE', name: 'EN', flag: '🇮🇪' },
  { code: 'es-ES', name: 'ES', flag: '🇪🇸' },
];

export function Topbar({ onSearchClick, notificationCount = 0, sidebarContent }: TopbarProps) {
  const { user, logout } = useFirebaseAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language') || 'pt-BR'
  );

  // Gerar iniciais do usuário para avatar
  const getUserInitials = (): string => {
    if (!user?.name) return '?';
    const names = user.name.trim().split(' ');
    if (names.length === 1) return names[0][0].toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  // Handler de busca (CMD/CTRL + K)
  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      // Fallback: dispatch keyboard event para trigger CommandMenu
      document.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true })
      );
    }
  };

  // Handler de logout
  const handleLogout = async () => {
    try {
      await logout();
      // Navegação será tratada pelo AuthGuard
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Handler de mudança de idioma
  const handleLanguageChange = (langCode: string) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('language', langCode);
    // Aqui poderia disparar um evento ou atualizar contexto de i18n
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: langCode }));
  };

  return (
    <header
      role='banner'
      className='fixed top-0 left-0 right-0 z-50 flex items-center justify-between h-[64px] px-4 sm:px-6
                 bg-white dark:bg-gray-900
                 border-b border-gray-200 dark:border-gray-800
                 shadow-brand-sm
                 transition-colors duration-brand-fast'
      data-testid='dashboard-topbar'
    >
      {/* ========================================
       * LOGO + BRANDING (LEFT SECTION)
       * ======================================== */}
      <div className='flex items-center gap-3'>
        {/* Mobile Menu Button (hamburguer) */}
        <Dialog.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <Dialog.Trigger asChild>
            <button
              className='lg:hidden p-2 rounded-brand-md
                         text-gray-700 dark:text-gray-300
                         hover:bg-gray-100 dark:hover:bg-gray-800
                         focus-brand
                         transition-colors duration-brand-fast'
              aria-label='Abrir menu de navegação'
              data-testid='mobile-menu-button'
            >
              <Menu className='w-5 h-5' />
            </button>
          </Dialog.Trigger>

          {/* Mobile Dialog - Sidebar */}
          <Dialog.Portal>
            <Dialog.Overlay
              className='fixed inset-0 z-50 bg-black/50 backdrop-blur-sm
                         data-[state=open]:animate-fadeIn'
            />
            <Dialog.Content
              className='fixed top-0 left-0 z-50 h-full w-sidebar
                         bg-white dark:bg-gray-900
                         border-r border-gray-200 dark:border-gray-800
                         shadow-brand-xl
                         data-[state=open]:animate-slideRight
                         data-[state=closed]:animate-slideLeft'
            >
              <div className='h-full overflow-y-auto p-6'>
                {sidebarContent || (
                  <div className='text-sm text-gray-500'>Sidebar content não fornecido</div>
                )}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Logo */}
        <Link href='/dashboard'>
          <a
            className='flex items-center gap-2 group'
            aria-label='ChatbotImóveis - Ir para dashboard'
          >
            <img
              src='/favicon.svg'
              alt='ChatbotImóveis'
              className='h-8 w-8 transition-transform duration-brand-normal group-hover:scale-105'
              loading='eager'
            />
            <span className='hidden md:block font-brand font-semibold text-lg text-gray-900 dark:text-white tracking-tight'>
              ChatbotImóveis<sup className='text-xs font-normal'>®</sup>
            </span>
          </a>
        </Link>
      </div>

      {/* ========================================
       * SEARCH BAR (CENTER SECTION - DESKTOP ONLY)
       * ======================================== */}
      <div className='hidden md:flex flex-1 max-w-md mx-6'>
        <button
          onClick={handleSearchClick}
          className='flex items-center gap-3 w-full px-4 py-2
                     bg-gray-100 dark:bg-gray-800
                     hover:bg-gray-200 dark:hover:bg-gray-700
                     border border-gray-200 dark:border-gray-700
                     rounded-brand-md
                     text-sm text-gray-500 dark:text-gray-400
                     focus-brand
                     transition-all duration-brand-fast
                     group'
          aria-label='Buscar (⌘K ou Ctrl+K)'
          data-testid='search-trigger'
        >
          <Search className='w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-brand-primary' />
          <span className='flex-1 text-left'>Buscar leads, contatos...</span>
          <kbd
            className='hidden sm:inline-flex items-center gap-1 px-2 py-1
                          bg-white dark:bg-gray-900
                          border border-gray-300 dark:border-gray-600
                          rounded text-xs font-mono text-gray-600 dark:text-gray-400'
          >
            <span className='text-xs'>⌘</span>K
          </kbd>
        </button>
      </div>

      {/* ========================================
       * ACTIONS (RIGHT SECTION)
       * ======================================== */}
      <nav className='flex items-center gap-2' role='navigation' aria-label='Ações principais'>
        {/* Search Icon (Mobile) */}
        <button
          onClick={handleSearchClick}
          className='md:hidden p-2 rounded-brand-md
                     text-gray-700 dark:text-gray-300
                     hover:bg-gray-100 dark:hover:bg-gray-800
                     focus-brand
                     transition-colors duration-brand-fast'
          aria-label='Buscar'
          data-testid='search-trigger-mobile'
        >
          <Search className='w-5 h-5' />
        </button>

        {/* WhatsApp Quick Access */}
        <Link href='/dashboard/whatsapp'>
          <a
            className='hidden sm:flex p-2 rounded-brand-md
                       text-gray-700 dark:text-gray-300
                       hover:bg-gray-100 dark:hover:bg-gray-800
                       hover:text-green-600 dark:hover:text-green-400
                       focus-brand
                       transition-colors duration-brand-fast'
            aria-label='WhatsApp Business'
            data-testid='whatsapp-button'
          >
            <MessageCircle className='w-5 h-5' />
          </a>
        </Link>

        {/* Notifications */}
        <Link href='/dashboard/notifications'>
          <a
            className='relative p-2 rounded-brand-md
                       text-gray-700 dark:text-gray-300
                       hover:bg-gray-100 dark:hover:bg-gray-800
                       focus-brand
                       transition-colors duration-brand-fast'
            aria-label={`Notificações${notificationCount > 0 ? ` (${notificationCount} não lidas)` : ''}`}
            data-testid='notifications-button'
          >
            <Bell className='w-5 h-5' />
            {notificationCount > 0 && (
              <span
                className='absolute top-1 right-1 flex items-center justify-center
                           min-w-[1.25rem] h-5 px-1.5
                           bg-red-500 text-white text-xs font-bold
                           rounded-full
                           animate-pulse'
                aria-label={`${notificationCount} notificações não lidas`}
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </a>
        </Link>

        {/* Language Selector */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className='p-2 rounded-brand-md
                         text-gray-700 dark:text-gray-300
                         hover:bg-gray-100 dark:hover:bg-gray-800
                         focus-brand
                         transition-colors duration-brand-fast
                         flex items-center gap-1.5'
              aria-label='Selecionar idioma'
              data-testid='language-selector'
            >
              <Globe className='w-5 h-5' />
              <span className='text-lg'>
                {LANGUAGES.find(l => l.code === currentLanguage)?.flag}
              </span>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align='end'
              sideOffset={8}
              className='min-w-[180px] p-2
                         bg-white dark:bg-gray-900
                         border border-gray-200 dark:border-gray-800
                         rounded-brand-lg
                         shadow-brand-xl
                         data-[state=open]:animate-slideDown
                         z-[100]'
            >
              {LANGUAGES.map(lang => (
                <DropdownMenu.Item key={lang.code} asChild>
                  <button
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`flex items-center gap-3 w-full px-3 py-2 rounded-brand-md
                               text-sm transition-colors duration-brand-fast
                               cursor-pointer
                               ${
                                 currentLanguage === lang.code
                                   ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                   : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                               }`}
                  >
                    <span className='text-lg'>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Theme Toggle */}
        <ThemeToggle variant='button' size='sm' />

        {/* User Profile Dropdown */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className='flex items-center gap-2 p-1.5 pl-3 pr-3 rounded-brand-md
                         bg-gray-100 dark:bg-gray-800
                         hover:bg-gray-200 dark:hover:bg-gray-700
                         focus-brand
                         transition-colors duration-brand-fast
                         group'
              aria-label='Menu do usuário'
              data-testid='user-menu-button'
            >
              {/* Avatar com iniciais */}
              <div
                className='flex items-center justify-center
                           w-8 h-8 rounded-full
                           bg-brand-gradient
                           text-white text-sm font-bold
                           shadow-brand-sm'
                aria-hidden='true'
              >
                {getUserInitials()}
              </div>
              {/* Nome (desktop only) */}
              <span className='hidden lg:block text-sm font-medium text-gray-900 dark:text-white'>
                {user?.name?.split(' ')[0] || 'Usuário'}
              </span>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align='end'
              sideOffset={8}
              className='min-w-[220px] p-2
                         bg-white dark:bg-gray-900
                         border border-gray-200 dark:border-gray-800
                         rounded-brand-lg
                         shadow-brand-xl
                         data-[state=open]:animate-slideDown
                         z-[100]'
            >
              {/* User Info Header */}
              <div className='px-3 py-2 mb-2 border-b border-gray-200 dark:border-gray-800'>
                <p className='text-sm font-semibold text-gray-900 dark:text-white truncate'>
                  {user?.name || 'Usuário'}
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>
                  {user?.email || 'email@exemplo.com'}
                </p>
              </div>

              {/* Menu Items */}
              <DropdownMenu.Item asChild>
                <Link href='/dashboard/profile'>
                  <a
                    className='flex items-center gap-3 px-3 py-2 rounded-brand-md
                               text-sm text-gray-700 dark:text-gray-300
                               hover:bg-gray-100 dark:hover:bg-gray-800
                               focus-brand
                               transition-colors duration-brand-fast
                               cursor-pointer'
                  >
                    <UserIcon className='w-4 h-4' />
                    Meu Perfil
                  </a>
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Item asChild>
                <Link href='/dashboard/settings'>
                  <a
                    className='flex items-center gap-3 px-3 py-2 rounded-brand-md
                               text-sm text-gray-700 dark:text-gray-300
                               hover:bg-gray-100 dark:hover:bg-gray-800
                               focus-brand
                               transition-colors duration-brand-fast
                               cursor-pointer'
                  >
                    <Settings className='w-4 h-4' />
                    Configurações
                  </a>
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className='h-px bg-gray-200 dark:bg-gray-800 my-2' />

              <DropdownMenu.Item asChild>
                <button
                  onClick={handleLogout}
                  className='flex items-center gap-3 w-full px-3 py-2 rounded-brand-md
                             text-sm text-red-600 dark:text-red-400
                             hover:bg-red-50 dark:hover:bg-red-900/20
                             focus-brand
                             transition-colors duration-brand-fast
                             cursor-pointer'
                  data-testid='logout-button'
                >
                  <LogOut className='w-4 h-4' />
                  Sair
                </button>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </nav>
    </header>
  );
}
